import React, { useId, useState } from 'react';
import { MediafileItem, MediafilePoster } from '@entities/media';
import IconTrash from '@zvuk-b2b/react-uikit/ui/icons/Trash2';
import IconRefresh from '@zvuk-b2b/react-uikit/ui/icons/RefreshCcw';
import MediafilePosterPreview from 'application/components/MediafilePosterPreview';
import FileUtils from 'application/utils/FileUtils';
import { getMediaPreviewKind } from 'application/utils/MediaPreviewUtils';
import VideoPromoField from '../VideoPromoField';
import {
  getVideoPromoDuration,
  getVideoPromoKind,
  parseVideoPromoDuration,
  VIDEO_PROMO_CLOCK_PATTERN,
} from '../videoPromoDraft';
import type { VideoPromoItemProps } from './types';
import { TEXTS } from './texts';
import styles from './index.module.scss';

const VideoPromoItem: React.FC<VideoPromoItemProps> = ({
  item,
  isExactTime,
  duration,
  time,
  isDisabled,
  leading,
  onDurationChange,
  onTimeChange,
  onRemove,
  onRetry,
}) => {
  const fallbackId = useId();
  const id = item.id ?? fallbackId;
  const [isExpanded, setIsExpanded] = useState(false);
  const kind = getVideoPromoKind(item) ?? 'video';
  const removeAction = {
    id: 'remove',
    kind: 'button' as const,
    label: TEXTS.REMOVE,
    icon: <IconTrash />,
    isDisabled,
    onClick: onRemove,
  };

  if (item.isLoading || item.error) {
    const state = item.isLoading ? 'uploading' : 'upload-error';
    return (
      <MediafileItem
        id={id}
        title={item.title}
        state={state}
        kind={kind}
        leading={leading}
        poster={
          item.isLoading ? (
            <MediafilePoster
              id={id}
              kind={kind}
              state="uploading"
              durationLabel="00:00:10"
            />
          ) : (
            <MediafilePoster
              id={id}
              kind={kind}
              state="upload-error"
            />
          )
        }
        statusMessage={item.isLoading ? TEXTS.UPLOADING : TEXTS.ERROR}
        actions={
          item.isLoading
            ? [removeAction]
            : [
                {
                  id: 'retry',
                  kind: 'button',
                  label: TEXTS.RETRY,
                  icon: <IconRefresh />,
                  isDisabled,
                  onClick: onRetry,
                },
                removeAction,
              ]
        }
      />
    );
  }

  const original = FileUtils.getOriginalMediafileSource(item.sourcefiles);
  const previewKind = getMediaPreviewKind({ type: item.type, extension: original?.extension });
  const hasSettings = kind === 'image' || isExactTime;
  const isValid =
    (kind !== 'image' || parseVideoPromoDuration(duration) !== undefined) &&
    (!isExactTime || VIDEO_PROMO_CLOCK_PATTERN.test(time));
  return (
    <MediafileItem
      id={id}
      title={item.title}
      state="ready"
      kind={kind}
      leading={leading}
      poster={
        <MediafilePosterPreview
          id={id}
          kind={kind}
          title={item.title}
          thumbnail={item.previewUrl}
          duration={kind === 'image' ? getVideoPromoDuration(item) : item.duration}
          extension={original?.extension}
          url={original?.url ?? undefined}
          isVideo={previewKind ? previewKind === 'video' : undefined}
        />
      }
      settings={
        hasSettings
          ? {
              panelId: `video_promo_settings_${id}`,
              label: TEXTS.SETTINGS,
              layout: 'responsive-actions',
              isExpanded,
              isDisabled,
              onExpandedChange: (expanded) => setIsExpanded(expanded || !isValid),
              content: (
                <div className={styles.fields}>
                  {kind === 'image' && (
                    <VideoPromoField
                      id={`video_promo_duration_${id}`}
                      kind="duration"
                      value={duration}
                      isDisabled={isDisabled}
                      onChange={onDurationChange}
                    />
                  )}
                  {isExactTime && (
                    <VideoPromoField
                      id={`video_promo_time_${id}`}
                      kind="time"
                      value={time}
                      isDisabled={isDisabled}
                      onChange={onTimeChange}
                    />
                  )}
                </div>
              ),
            }
          : undefined
      }
      actions={[removeAction]}
    />
  );
};

export default VideoPromoItem;
